import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import LanguageSelect from "../shared/LanguageSelect";
import MessagePreviewDialog from "../shared/MessagePreviewDialog";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useUserStore } from "store";
import { getCsrfToken } from "../../utils";
import { bicConflictsWithIban, expectedBicForIban } from "../../utils/bank";

function OrganizationInvoiceSettingsTab({ t, onDirtyChange }) {
  const { currentOrganization, patchCurrentOrganization } = useUserStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organization, setOrganizationRaw] = useState(null);
  // Which customer message is being previewed: "offer_email" | "payment_reminder" | null.
  const [previewKind, setPreviewKind] = useState(null);

  // The message rendered with the texts on screen, saved or not (one endpoint, one kind each).
  const loadMessagePreview = (kind, fields) => async (language) => {
    const draft = {};
    fields.forEach((field) => {
      if (organization?.[field] !== undefined && organization?.[field] !== null) draft[field] = organization[field];
    });
    const res = await axios.post(
      `/api/organizations/${currentOrganization.organization_id}/message-preview/`,
      { kind, draft, language },
      { headers: { "X-Organization-Id": currentOrganization.organization_id } },
    );
    return res.data;
  };
  const [isDirty, setIsDirty] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (currentOrganization?.organization_id) {
      fetchOrganization();
    }
  }, [currentOrganization?.organization_id]);

  const fetchOrganization = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/organizations/${currentOrganization.organization_id}/`,
        {
          headers: { "X-Organization-Id": currentOrganization.organization_id },
        }
      );
      setOrganizationRaw(response.data);
      setIsDirty(false);
    } catch (error) {
      console.error("Failed to fetch organization:", error);
      setSnackbar({
        open: true,
        message: t("Failed to load organization data"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const setOrganization = useCallback((updater) => {
    setOrganizationRaw(updater);
    setIsDirty(true);
  }, []);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setOrganization((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSwitchChange = (field) => (event) => {
    const value = event.target.checked;
    setOrganization((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.patch(
        `/api/organizations/${organization.id}/`,
        {
          iban: organization.iban,
          bic: organization.bic,
          vat_number: organization.vat_number,
          invoice_email: organization.invoice_email,
          invoice_footer_text: organization.invoice_footer_text,
          invoice_number_prefix: organization.invoice_number_prefix,
          default_payment_terms_days: organization.default_payment_terms_days,
          default_invoice_language: organization.default_invoice_language,
          insurance_seller_site_code: organization.insurance_seller_site_code,
          invoice_parts_tab_first: organization.invoice_parts_tab_first,
          show_part_codes_on_invoices: organization.show_part_codes_on_invoices,
          document_unit_price_mode: organization.document_unit_price_mode,
          parts_first_on_documents: organization.parts_first_on_documents,
          group_subtotals_on_documents: organization.group_subtotals_on_documents,
          offer_validity_days: organization.offer_validity_days,
          offer_additional_text_fi: organization.offer_additional_text_fi,
          offer_additional_text_en: organization.offer_additional_text_en,
          payment_reminders_enabled: organization.payment_reminders_enabled,
          payment_reminder_fee: organization.payment_reminder_fee,
          payment_reminder_due_days: organization.payment_reminder_due_days,
          payment_reminder_text_fi: organization.payment_reminder_text_fi,
          payment_reminder_text_en: organization.payment_reminder_text_en,
          payment_reminder_text_sv: organization.payment_reminder_text_sv,
        },
        {
          headers: {
            "X-CSRFToken": getCsrfToken(),
            "X-Organization-Id": currentOrganization.organization_id,
          },
        }
      );
      // The customer card shows this as the fallback maksuehto, so keep the cached org in step.
      patchCurrentOrganization({ default_payment_terms_days: Number(organization.default_payment_terms_days) });
      setIsDirty(false);
      setSnackbar({
        open: true,
        message: t("Invoice settings updated successfully"),
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to update invoice settings:", error);
      const message =
        error.response?.data?.detail || t("Failed to update invoice settings");
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = isDirty;

  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  useEffect(() => {
    onDirtyChange?.(hasChanges, () => handleSaveRef.current());
  }, [hasChanges, onDirtyChange]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!organization) {
    return (
      <Alert severity="error">{t("Failed to load organization data")}</Alert>
    );
  }

  // Mirrors the backend gate: the flag alone is not "uses Amili", an activation can be
  // pending or failed. Amili already runs reminders and collection, so in-house reminders
  // would be a second dunning track on the same receivable.
  const amiliInUse =
    !!organization.amili_enabled && organization.amili_status === "active";

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Bank Information */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "text.secondary" }}>
            {t("Bank Information")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("IBAN")}
            value={organization.iban || ""}
            onChange={handleChange("iban")}
            size="small"
            placeholder="FI00 0000 0000 0000 00"
            helperText={t("Bank account for invoice payments")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("BIC/SWIFT")}
            value={organization.bic || ""}
            onChange={handleChange("bic")}
            size="small"
            placeholder="NDEAFIHH"
            // The recipient's bank rejects an e-invoice whose BIC and IBAN disagree, so say
            // it here rather than let the invoice bounce hours after it looked delivered.
            error={bicConflictsWithIban(organization.iban, organization.bic)}
            helperText={
              bicConflictsWithIban(organization.iban, organization.bic)
                ? t("settings.bicDoesNotMatchIban", {
                    defaultValue:
                      "BIC-koodi ei vastaa tilinumeroa. IBANin perusteella sen pitäisi olla {{expectedBic}}.",
                    expectedBic: expectedBicForIban(organization.iban),
                  })
                : !organization.bic && expectedBicForIban(organization.iban)
                  ? t("settings.bicSuggestion", {
                      defaultValue: "Tilinumeron perusteella BIC-koodi on {{expectedBic}}.",
                      expectedBic: expectedBicForIban(organization.iban),
                    })
                  : t("settings.bicRequiredForEinvoices", "Vaaditaan verkkolaskujen lähettämiseen")
            }
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("VAT Number")}
            value={organization.vat_number || ""}
            onChange={handleChange("vat_number")}
            size="small"
            placeholder="FI12345678"
            helperText={t("Finnish ALV-numero")}
          />
        </Grid>

        {/* Insurance Invoicing */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, mt: 2, fontWeight: 600, color: "text.secondary" }}
          >
            {t("Insurance invoicing")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("Workshop identifier (for insurance invoices)")}
            value={organization.insurance_seller_site_code || ""}
            onChange={handleChange("insurance_seller_site_code")}
            size="small"
            helperText={t("insuranceSellerSiteCodeHelperText")}
          />
        </Grid>

        {/* Invoice Settings */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, mt: 2, fontWeight: 600, color: "text.secondary" }}
          >
            {t("Invoice Settings")}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label={t("Invoice Number Prefix")}
            value={organization.invoice_number_prefix || ""}
            onChange={(e) => {
              // Only allow digits
              const value = e.target.value.replace(/\D/g, '');
              handleChange("invoice_number_prefix")({ target: { value } });
            }}
            size="small"
            placeholder="001"
            helperText={t("Optional, digits only")}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
        </Grid>

        <Grid item xs={6} sm={6} md={4}>
          <TextField
            fullWidth
            label={t("Default Payment Terms")}
            type="number"
            value={organization.default_payment_terms_days || 14}
            onChange={handleChange("default_payment_terms_days")}
            size="small"
            InputProps={{
              endAdornment: <Typography color="text.secondary">{t("days")}</Typography>,
            }}
          />
        </Grid>

        <Grid item xs={6} sm={6} md={4}>
          <LanguageSelect
            fullWidth
            size="small"
            value={organization.default_invoice_language || "fi"}
            onChange={handleChange("default_invoice_language")}
            label={t("Default Invoice Language")}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={organization.invoice_parts_tab_first || false}
                onChange={handleSwitchChange("invoice_parts_tab_first")}
              />
            }
            label={t("Show parts tab first in add invoice line")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("When enabled, the parts tab is shown first when adding invoice lines")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={organization.show_part_codes_on_invoices || false}
                onChange={handleSwitchChange("show_part_codes_on_invoices")}
              />
            }
            label={t("Show part codes on invoices")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("When enabled, part/product codes are shown on invoices and e-invoices sent to customers. Hidden by default.")}
          </Typography>
        </Grid>


        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={organization.parts_first_on_documents || false}
                onChange={handleSwitchChange("parts_first_on_documents")}
              />
            }
            label={t("List parts before labour on documents")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("Documents list labour before parts by default. Applies to invoices, receipts, quotes and work orders.")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={organization.group_subtotals_on_documents || false}
                onChange={handleSwitchChange("group_subtotals_on_documents")}
              />
            }
            label={t("Group labour and parts with subtotals on documents")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {t("Prints a heading for labour and for parts with a subtotal after each group. Applies to invoices, receipts, quotes and priced work orders.")}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={8} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>{t("Unit price display on documents")}</InputLabel>
            <Select
              value={organization.document_unit_price_mode || "excl_vat"}
              onChange={handleChange("document_unit_price_mode")}
              label={t("Unit price display on documents")}
            >
              <MenuItem value="excl_vat">{t("Unit price excl. VAT, with a VAT % column")}</MenuItem>
              <MenuItem value="net_rows_with_column">{t("Row prices excl. VAT (unit price and row total), with a VAT % column")}</MenuItem>
              <MenuItem value="net_rows">{t("Row prices excl. VAT (unit price and row total), no VAT % column")}</MenuItem>
              <MenuItem value="incl_vat_with_column">{t("Unit price incl. VAT, with a VAT % column")}</MenuItem>
              <MenuItem value="incl_vat">{t("Unit price incl. VAT, no VAT % column")}</MenuItem>
              <MenuItem value="hidden">{t("No unit price and no VAT % column")}</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t("Applies to invoices, receipts, quotes, priced work orders and delivery notes. VAT is always itemised per rate in the totals.")}
          </Typography>
        </Grid>

        {/* Email Settings */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, mt: 2, fontWeight: 600, color: "text.secondary" }}
          >
            {t("Email Settings")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("Invoice Email (From)")}
            type="email"
            value={organization.invoice_email || ""}
            onChange={handleChange("invoice_email")}
            size="small"
            placeholder="laskutus@yritys.fi"
            helperText={t("Sender address for invoice emails")}
          />
        </Grid>

        {/* Invoice Footer */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, mt: 2, fontWeight: 600, color: "text.secondary" }}
          >
            {t("Invoice Footer")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t("Footer Text")}
            value={organization.invoice_footer_text || ""}
            onChange={handleChange("invoice_footer_text")}
            size="small"
            multiline
            rows={3}
            placeholder={t("Additional text to display at bottom of invoices")}
            helperText={t("e.g. Payment terms, bank details, or thank you message")}
          />
        </Grid>

        {/* Quote Settings */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, mt: 2, fontWeight: 600, color: "text.secondary" }}
          >
            {t("Quote Settings")}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label={t("Default offer validity (days)")}
            type="number"
            value={organization.offer_validity_days ?? 14}
            onChange={handleChange("offer_validity_days")}
            size="small"
            slotProps={{ input: { inputProps: { min: 0 } } }}
            helperText={t("Set to 0 to hide validity section from quotes")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("Additional text on quotes (Finnish)")}
            value={organization.offer_additional_text_fi || ""}
            onChange={handleChange("offer_additional_text_fi")}
            size="small"
            multiline
            rows={2}
            helperText={t("Shown below validity on Finnish quotes. Leave empty to omit.")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("Additional text on quotes (English)")}
            value={organization.offer_additional_text_en || ""}
            onChange={handleChange("offer_additional_text_en")}
            size="small"
            multiline
            rows={2}
            helperText={t("Shown below validity on English quotes. Leave empty to omit.")}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t("Additional text on quotes (Swedish)")}
            value={organization.offer_additional_text_sv || ""}
            onChange={handleChange("offer_additional_text_sv")}
            size="small"
            multiline
            rows={2}
            helperText={t("Shown below validity on Swedish quotes. Leave empty to omit.")}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => setPreviewKind("offer_email")}
            data-testid="offer-email-preview-button"
          >
            {t("Preview quote email")}
          </Button>
        </Grid>

        {/* Payment reminders (maksumuistutus) */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, mt: 2, fontWeight: 600, color: "text.secondary" }}
          >
            {t("Payment reminders")}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={!!organization.payment_reminders_enabled}
                onChange={handleSwitchChange("payment_reminders_enabled")}
                disabled={amiliInUse}
              />
            }
            label={t("Payment reminders in use")}
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6, mt: -0.5 }}>
            {amiliInUse
              ? t("Amili handles payment monitoring and reminders for this organization.")
              : t("Allows sending a payment reminder for an overdue invoice of a company customer.")}
          </Typography>
        </Grid>

        {organization.payment_reminders_enabled && !amiliInUse && (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label={t("Reminder fee (EUR)")}
                type="number"
                value={organization.payment_reminder_fee ?? "0.00"}
                onChange={handleChange("payment_reminder_fee")}
                size="small"
                slotProps={{ input: { inputProps: { min: 0, max: 12, step: 0.5 } } }}
                helperText={t("Finnish law caps this at 12 euros per reminder. 0 charges nothing.")}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label={t("New payment period (days)")}
                type="number"
                value={organization.payment_reminder_due_days ?? 7}
                onChange={handleChange("payment_reminder_due_days")}
                size="small"
                slotProps={{ input: { inputProps: { min: 1 } } }}
                helperText={t("Days from the reminder date to the new due date")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("Additional text on reminders (Finnish)")}
                value={organization.payment_reminder_text_fi || ""}
                onChange={handleChange("payment_reminder_text_fi")}
                size="small"
                multiline
                rows={2}
                helperText={t("Shown at the bottom of Finnish reminders. Leave empty to omit.")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("Additional text on reminders (English)")}
                value={organization.payment_reminder_text_en || ""}
                onChange={handleChange("payment_reminder_text_en")}
                size="small"
                multiline
                rows={2}
                helperText={t("Shown at the bottom of English reminders. Leave empty to omit.")}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("Additional text on reminders (Swedish)")}
                value={organization.payment_reminder_text_sv || ""}
                onChange={handleChange("payment_reminder_text_sv")}
                size="small"
                multiline
                rows={2}
                helperText={t("Shown at the bottom of Swedish reminders. Leave empty to omit.")}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon />}
                onClick={() => setPreviewKind("payment_reminder")}
                data-testid="payment-reminder-preview-button"
              >
                {t("Preview payment reminder")}
              </Button>
            </Grid>
          </>
        )}

        {/* Current Invoice Number */}
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            sx={{ mb: 2, mt: 2, fontWeight: 600, color: "text.secondary" }}
          >
            {t("Invoice Numbering")}
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("Next Invoice Number")}
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
            {organization.invoice_number_prefix || ""}{new Date().getFullYear()}{String(organization.next_invoice_number || 1).padStart(4, "0")}
          </Typography>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={20} /> : <span>{t("Save Changes")}</span>}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <MessagePreviewDialog
        open={previewKind !== null}
        onClose={() => setPreviewKind(null)}
        title={previewKind === "payment_reminder" ? t("Payment reminder preview") : t("Quote email preview")}
        defaultLanguage={currentOrganization?.default_language}
        loadPreview={
          previewKind === "payment_reminder"
            ? loadMessagePreview("payment_reminder", [
                "payment_reminder_fee",
                "payment_reminder_due_days",
                "payment_reminder_text_fi",
                "payment_reminder_text_en",
                "payment_reminder_text_sv",
              ])
            : loadMessagePreview("offer_email", [
                "offer_validity_days",
                "offer_additional_text_fi",
                "offer_additional_text_en",
                "offer_additional_text_sv",
              ])
        }
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withTranslation()(OrganizationInvoiceSettingsTab);
